// design-system-exempt: the swatch list below is DATA — colours offered to a team
// to choose from, not colours this UI paints itself in.
// What this deployment calls itself. Admin-only, and the reason a customer never
// has to redeploy to change their own name or colour.
//
// Deliberately four fields. A team should be able to make this theirs in under a
// minute; a theme editor is a different product and it is not this one. The accent
// is a single colour because the rest of the palette is derived from it, so there
// is no way to pick a combination that reads badly.

import { useEffect, useState } from 'react'

import { Button, Section } from '../components/common'
import { Icon } from '../components/Icon'
import { api, blobUrl } from '../lib/api'
import { DEFAULT_BRAND, type Brand } from '../lib/brand'
import { useStore } from '../lib/store'
import { applyBrand } from '../lib/theme'

/** A few defaults worth offering, so nobody has to think in hex to get started. */
const SUGGESTED = ['#3b5bdb', '#0b7285', '#2b8a3e', '#c2255c', '#e8590c', '#5f3dc4', '#343a40']

export function BrandingPanel() {
  const { brand, saveBrand, admin } = useStore()
  const [draft, setDraft] = useState<Brand>(brand)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  // Someone else may change the brand while this page is open.
  useEffect(() => setDraft(brand), [brand])

  // Preview live as you type, so the accent is judged in place rather than as a
  // swatch. Reverted on unmount if it was never saved.
  useEffect(() => {
    applyBrand(draft)
    return () => applyBrand(brand)
  }, [draft, brand])

  const dirty = JSON.stringify(draft) !== JSON.stringify(brand)

  async function save() {
    setBusy(true)
    setError('')
    try {
      await saveBrand(draft)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1600)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  async function uploadLogo(file: File | undefined) {
    if (!file) return
    setError('')
    if (file.size > 512 * 1024) {
      setError('That logo is over 512 KB. A header icon needs far less than that.')
      return
    }
    setBusy(true)
    try {
      // Content-addressed by time, so a replacement is a new key and the old one
      // stays cacheable forever rather than needing a bust.
      const ext = (file.name.split('.').pop() ?? 'png').toLowerCase().replace(/[^a-z0-9]/g, '')
      const key = await api.putBlob(`brand/logo-${Date.now()}.${ext}`, file)
      setDraft((d) => ({ ...d, logoKey: key }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The upload failed.')
    } finally {
      setBusy(false)
    }
  }

  // The server refuses a non-admin write anyway; hiding the panel just avoids
  // offering an action that would be rejected.
  if (!admin) return null

  const field = {
    borderColor: 'var(--border)',
    background: 'var(--panel)',
    color: 'var(--fg)',
  }

  return (
    <Section
      title="Branding"
      note="what this deployment calls itself"
      right={
        dirty ? (
          <Button variant="work" onClick={() => void save()}>
            <Icon name="Check" size={14} /> {busy ? 'Saving…' : 'Save'}
          </Button>
        ) : saved ? (
          <span className="pill" style={{ '--pc': 'var(--good)' } as React.CSSProperties}>
            <span className="tick" /> saved
          </span>
        ) : undefined
      }
    >
      {error && (
        <p className="text-body mb-2" style={{ color: 'var(--crit)' }}>
          {error}
        </p>
      )}

      <label className="flex flex-col gap-1 py-2">
        <span className="eyebrow">Name</span>
        <input
          value={draft.instanceName}
          onChange={(e) => setDraft({ ...draft, instanceName: e.target.value })}
          placeholder="Knowledge Futures Projects"
          className="rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
        />
        <span className="note">Shown in the header and the browser tab.</span>
      </label>

      <div className="flex flex-col gap-1 py-2">
        <span className="eyebrow">Accent</span>
        <div className="flex flex-wrap items-center gap-2">
          {SUGGESTED.map((hex) => (
            <button
              key={hex}
              type="button"
              title={hex}
              onClick={() => setDraft({ ...draft, accent: hex })}
              className="h-6 w-6 rounded-full"
              style={{
                background: hex,
                outline: draft.accent === hex ? `2px solid ${hex}` : 'none',
                outlineOffset: 2,
              }}
            />
          ))}
          <input
            type="color"
            value={draft.accent}
            onChange={(e) => setDraft({ ...draft, accent: e.target.value })}
            className="rounded-inline h-6 w-10 cursor-pointer border bg-transparent"
            style={{ borderColor: 'var(--border)' }}
          />
          <code className="meta">{draft.accent}</code>
        </div>
        <span className="note">Used for links, highlights and the mark.</span>
      </div>

      <div className="flex flex-col gap-1 py-2">
        <span className="eyebrow">Logo</span>
        <div className="flex flex-wrap items-center gap-3">
          {draft.logoKey ? (
            <img
              src={blobUrl(draft.logoKey)}
              alt=""
              className="h-9 w-9 rounded-[30%] object-contain"
              style={{ background: 'var(--panel)' }}
            />
          ) : (
            <span className="meta">none</span>
          )}
          <label
            className="rounded-control text-body cursor-pointer border px-2.5 py-1.5"
            style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
          >
            {draft.logoKey ? 'Replace' : 'Upload'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={(e) => void uploadLogo(e.target.files?.[0])}
            />
          </label>
          {draft.logoKey && (
            <Button
              variant="quiet"
              size="dense"
              onClick={() => setDraft({ ...draft, logoKey: '' })}
            >
              Remove
            </Button>
          )}
        </div>
        <span className="note">A square mark reads best. Under 512 KB.</span>
      </div>

      <label className="flex flex-col gap-1 py-2">
        <span className="eyebrow">Sign-in note</span>
        <input
          value={draft.loginBlurb}
          onChange={(e) => setDraft({ ...draft, loginBlurb: e.target.value })}
          placeholder="Staff only. Ask an admin if you need access."
          className="rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
        />
        <span className="note">One line under the sign-in button. Optional.</span>
      </label>

      {dirty && (
        <button
          type="button"
          onClick={() => setDraft(brand.instanceName ? brand : DEFAULT_BRAND)}
          className="meta mt-1 underline"
        >
          discard changes
        </button>
      )}
    </Section>
  )
}
